import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ADMIN_ROUTES } from '../../constants';
import { ConfirmDialog } from '../../../shared/components';
import { adminApiService } from '../../services/adminApi';
import {
    LayoutDashboard,
    Users,
    Building2,
    Briefcase,
    Settings,
    BarChart2,
    Shield,
    Sparkles,
    MessageCircle
} from 'lucide-react';
import Sidebar, { SidebarMenuItem } from '../../../shared/components/navigation/Sidebar';

interface AdminSidebarProps {
    isCollapsed?: boolean;
    onToggle?: () => void;
    isMobileOpen?: boolean;
    onMobileClose?: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({
    isCollapsed = false,
    onToggle,
    isMobileOpen = false,
    onMobileClose
}) => {
    const navigate = useNavigate();
    const [showLogoutDialog, setShowLogoutDialog] = useState(false);
    const [pendingCandidatesCount, setPendingCandidatesCount] = useState<number>(0);
    const [pendingCompaniesCount, setPendingCompaniesCount] = useState<number>(0);

    // Fetch pending counts
    useEffect(() => {
        const fetchCounts = async () => {
            try {
                const reviews = await adminApiService.getPendingReviews();
                const candidates = reviews.filter(r => r.type === 'candidate').length;
                const companies = reviews.filter(r => r.type === 'company').length;
                setPendingCandidatesCount(candidates);
                setPendingCompaniesCount(companies);
            } catch (error) {
                console.error('Failed to fetch pending counts:', error);
            }
        };

        fetchCounts();
        // Refresh counts every 30 seconds
        const interval = setInterval(fetchCounts, 30000);
        return () => clearInterval(interval);
    }, []);

    const handleLogoutClick = () => {
        setShowLogoutDialog(true);
    };

    const handleLogoutConfirm = () => {
        // Clear all authentication and session data
        localStorage.clear();
        sessionStorage.clear();

        // Navigate to login page
        navigate('/auth/login');

        setShowLogoutDialog(false);
    };

    const handleLogoutCancel = () => {
        setShowLogoutDialog(false);
    };

    const menuItems: SidebarMenuItem[] = [
        {
            icon: (active) => <LayoutDashboard size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'Dashboard',
            path: ADMIN_ROUTES.DASHBOARD,
            show: true
        },
        {
            icon: (active) => <Users size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'Review Candidates',
            path: ADMIN_ROUTES.REVIEW_CANDIDATES,
            badge: pendingCandidatesCount || null,
            show: true
        },
        {
            icon: (active) => <Building2 size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'Review Companies',
            path: ADMIN_ROUTES.REVIEW_COMPANIES,
            badge: pendingCompaniesCount || null,
            show: true
        },
        {
            icon: (active) => <Sparkles size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'Recommend Jobs',
            path: ADMIN_ROUTES.CANDIDATE_RECOMMENDATIONS,
            show: true
        },
        {
            icon: (active) => <MessageCircle size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'David Inbox',
            path: ADMIN_ROUTES.DAVID_INBOX,
            show: true
        },
        {
            icon: (active) => <Briefcase size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'Find Candidates',
            path: ADMIN_ROUTES.MANAGE_JOBS,
            show: true
        },
        {
            icon: (active) => <Shield size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'User Management',
            path: ADMIN_ROUTES.USER_MANAGEMENT,
            show: true
        },
        {
            icon: (active) => <BarChart2 size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'Analytics',
            path: ADMIN_ROUTES.ANALYTICS,
            show: true
        },
        {
            icon: (active) => <Settings size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'System Settings',
            path: ADMIN_ROUTES.SYSTEM_SETTINGS,
            show: true
        }
    ];

    return (
        <>
            <Sidebar
                isCollapsed={isCollapsed}
                onToggle={onToggle || (() => { })}
                isMobileOpen={isMobileOpen}
                onMobileClose={onMobileClose || (() => { })}
                menuItems={menuItems}
                onLogout={handleLogoutClick}
            />

            <ConfirmDialog
                isOpen={showLogoutDialog}
                variant="info"
                title="Log Out?"
                message="You'll be signed out of your Hush account on this device."
                confirmText="Log Out"
                cancelText="Cancel"
                onConfirm={handleLogoutConfirm}
                onCancel={handleLogoutCancel}
            />
        </>
    );
};

export default AdminSidebar;
